import { useCallback, useEffect, useState, type DragEvent, type FormEvent } from "react";
import { HOME_TARGETS, type HomeSection, type HomeTarget } from "@kidtube/shared";
import { api } from "../api";
import { EmptyState } from "../components/Feedback";
import { formatDuration, moveItem } from "../utils";

interface HomeScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
}

interface DragState {
  kind: "section" | "video";
  sectionId?: string;
  index: number;
}

export function HomeScreen({ showSuccess, showError }: HomeScreenProps) {
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [videoInputs, setVideoInputs] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSections(await api.home());
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createSection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = newTitle.trim();
    if (title.length < 2) return;
    setBusy("create");
    try {
      const section = await api.createSection(title);
      setSections((current) => [...current, section]);
      setNewTitle("");
      showSuccess(`Đã tạo section “${title}”.`);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function saveTitle() {
    if (editing === null || editing.title.trim().length < 2) return;
    setBusy(editing.id);
    try {
      const title = editing.title.trim();
      await api.updateSection(editing.id, { title });
      setSections((current) =>
        current.map((section) => (section.id === editing.id ? { ...section, title } : section)),
      );
      setEditing(null);
      showSuccess("Đã đổi tên section.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function setTarget(section: HomeSection, target: HomeTarget) {
    setBusy(section.id);
    try {
      await api.updateSection(section.id, { target });
      setSections((current) => current.map((item) => item.id === section.id ? { ...item, target } : item));
      showSuccess("Đã đổi thiết bị hiển thị.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function toggleSection(section: HomeSection) {
    setBusy(section.id);
    try {
      await api.updateSection(section.id, { enabled: !section.enabled });
      setSections((current) =>
        current.map((item) =>
          item.id === section.id ? { ...item, enabled: !section.enabled } : item,
        ),
      );
      showSuccess(section.enabled ? "Đã ẩn section khỏi TV." : "Đã bật section trên TV.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function deleteSection(section: HomeSection) {
    if (!window.confirm(`Xóa section “${section.title}” và toàn bộ liên kết video trong section?`)) {
      return;
    }
    setBusy(section.id);
    try {
      await api.deleteSection(section.id);
      setSections((current) => current.filter((item) => item.id !== section.id));
      showSuccess("Đã xóa section.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function persistSectionOrder(next: HomeSection[]) {
    setSections(next);
    setBusy("section-order");
    try {
      await api.reorderSections(next.map((section) => section.id));
      showSuccess("Đã lưu thứ tự section.");
    } catch (error) {
      showError(error);
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function moveSection(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    await persistSectionOrder(moveItem(sections, index, target));
  }

  async function addVideo(section: HomeSection, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const video = (videoInputs[section.id] ?? "").trim();
    if (video.length === 0) return;
    setBusy(`${section.id}:add`);
    try {
      const added = await api.addVideo(section.id, video);
      setSections((current) =>
        current.map((item) =>
          item.id === section.id ? { ...item, videos: [...item.videos, added] } : item,
        ),
      );
      setVideoInputs((current) => ({ ...current, [section.id]: "" }));
      showSuccess(`Đã thêm “${added.title}” vào ${section.title}.`);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function removeVideo(section: HomeSection, videoId: string) {
    setBusy(`${section.id}:${videoId}`);
    try {
      await api.removeVideo(section.id, videoId);
      setSections((current) =>
        current.map((item) =>
          item.id === section.id
            ? { ...item, videos: item.videos.filter((video) => video.videoId !== videoId) }
            : item,
        ),
      );
      showSuccess("Đã gỡ video khỏi section.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  async function persistVideoOrder(section: HomeSection, videos: HomeSection["videos"]) {
    setSections((current) =>
      current.map((item) => (item.id === section.id ? { ...item, videos } : item)),
    );
    setBusy(`${section.id}:order`);
    try {
      await api.reorderVideos(section.id, videos.map((video) => video.videoId));
      showSuccess(`Đã lưu thứ tự video trong ${section.title}.`);
    } catch (error) {
      showError(error);
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function moveVideo(section: HomeSection, index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= section.videos.length) return;
    await persistVideoOrder(section, moveItem(section.videos, index, target));
  }

  function startDrag(event: DragEvent, state: DragState) {
    setDrag(state);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", `${state.kind}:${state.index}`);
  }

  async function dropSection(event: DragEvent, targetIndex: number) {
    event.preventDefault();
    if (drag?.kind !== "section" || drag.index === targetIndex) return;
    const next = moveItem(sections, drag.index, targetIndex);
    setDrag(null);
    await persistSectionOrder(next);
  }

  async function dropVideo(event: DragEvent, section: HomeSection, targetIndex: number) {
    event.preventDefault();
    if (
      drag?.kind !== "video" ||
      drag.sectionId !== section.id ||
      drag.index === targetIndex
    ) return;
    const next = moveItem(section.videos, drag.index, targetIndex);
    setDrag(null);
    await persistVideoOrder(section, next);
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">PARENT-CURATED HOME</p>
          <h1>Home Content</h1>
          <p>100% video trên Home do Parent chọn. KidTube không dùng recommendation của YouTube.</p>
        </div>
      </header>

      <form className="inline-create" onSubmit={createSection}>
        <label htmlFor="new-section">Tạo section mới</label>
        <div>
          <input
            id="new-section"
            value={newTitle}
            onChange={(event) => setNewTitle(event.target.value)}
            placeholder="Ví dụ: Toán vui"
            minLength={2}
            maxLength={80}
            required
          />
          <button type="submit" className="primary-button" disabled={busy !== null || newTitle.trim().length < 2}>+ Tạo section</button>
        </div>
      </form>

      <div className="section-heading compact">
        <div><h2>Các hàng trên TV</h2><p>Kéo-thả trên PC hoặc dùng nút ↑ ↓ trên điện thoại.</p></div>
      </div>

      {loading && sections.length === 0 ? (
        <div className="content-section-list"><div className="skeleton skeleton-section" /><div className="skeleton skeleton-section" /></div>
      ) : sections.length === 0 ? (
        <EmptyState icon="▦" title="Chưa có section" description="Tạo section đầu tiên rồi thêm video bằng Search YouTube hoặc Video ID/URL." />
      ) : (
        <div className="content-section-list">
          {sections.map((section, sectionIndex) => (
            <article
              className={section.enabled ? "content-section-card" : "content-section-card disabled-section"}
              key={section.id}
              draggable={busy === null}
              onDragStart={(event) => startDrag(event, { kind: "section", index: sectionIndex })}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => void dropSection(event, sectionIndex)}
            >
              <header className="content-section-header">
                <span className="drag-handle" aria-hidden="true">⠿</span>
                <span className="order-number">{sectionIndex + 1}</span>
                <div className="section-title-copy">
                  {editing?.id === section.id ? (
                    <div className="inline-edit">
                      <input
                        value={editing.title}
                        onChange={(event) => setEditing({ id: section.id, title: event.target.value })}
                        maxLength={80}
                        autoFocus
                      />
                      <button type="button" onClick={() => void saveTitle()} disabled={busy !== null}>Lưu</button>
                      <button type="button" onClick={() => setEditing(null)}>Hủy</button>
                    </div>
                  ) : (
                    <><h3>{section.title}</h3><p>{section.videos.length} video · {section.enabled ? "Đang hiển thị" : "Đang ẩn"}</p></>
                  )}
                </div>
                <div className="section-tools">
                  <select aria-label="Thiết bị hiển thị" value={section.target} onChange={(event) => void setTarget(section, event.target.value as HomeTarget)} disabled={busy !== null}>
                    {HOME_TARGETS.map((target) => <option key={target} value={target}>{target}</option>)}
                  </select>
                  <button type="button" className="icon-button" onClick={() => void moveSection(sectionIndex, -1)} disabled={sectionIndex === 0 || busy !== null} title="Di chuyển lên">↑</button>
                  <button type="button" className="icon-button" onClick={() => void moveSection(sectionIndex, 1)} disabled={sectionIndex === sections.length - 1 || busy !== null} title="Di chuyển xuống">↓</button>
                  <button type="button" className="icon-button" onClick={() => setEditing({ id: section.id, title: section.title })} disabled={busy !== null} title="Đổi tên">✎</button>
                  <label className="switch" title={section.enabled ? "Ẩn khỏi TV" : "Hiện trên TV"}>
                    <input type="checkbox" checked={section.enabled} onChange={() => void toggleSection(section)} disabled={busy !== null} />
                    <span />
                  </label>
                  <button type="button" className="icon-button danger-text" onClick={() => void deleteSection(section)} disabled={busy !== null} title="Xóa">×</button>
                </div>
              </header>

              {section.videos.length > 0 ? (
                <div className="managed-video-list">
                  {section.videos.map((video, videoIndex) => (
                    <div
                      className="managed-video"
                      key={video.videoId}
                      draggable={busy === null}
                      onDragStart={(event) => {
                        event.stopPropagation();
                        startDrag(event, { kind: "video", sectionId: section.id, index: videoIndex });
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.stopPropagation();
                        void dropVideo(event, section, videoIndex);
                      }}
                    >
                      <span className="drag-handle" aria-hidden="true">⠿</span>
                      <span className="video-order">{videoIndex + 1}</span>
                      <div className="small-thumbnail">
                        <img src={video.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                        <small>{formatDuration(video.durationSeconds)}</small>
                      </div>
                      <div className="managed-video-copy"><strong>{video.title}</strong><small>{video.channelName}</small></div>
                      <div className="managed-video-tools">
                        <button type="button" onClick={() => void moveVideo(section, videoIndex, -1)} disabled={videoIndex === 0 || busy !== null}>↑</button>
                        <button type="button" onClick={() => void moveVideo(section, videoIndex, 1)} disabled={videoIndex === section.videos.length - 1 || busy !== null}>↓</button>
                        <button type="button" className="danger-text" onClick={() => void removeVideo(section, video.videoId)} disabled={busy !== null}>Gỡ</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="section-empty">Chưa có video. Thêm Video ID/URL bên dưới hoặc dùng trang Tìm YouTube.</div>
              )}

              <form className="add-video-form" onSubmit={(event) => void addVideo(section, event)}>
                <input
                  value={videoInputs[section.id] ?? ""}
                  onChange={(event) => setVideoInputs((current) => ({ ...current, [section.id]: event.target.value }))}
                  placeholder="Dán link video HOẶC link kênh (vd: https://www.youtube.com/vtv7kids)"
                  maxLength={500}
                  required
                />
                <button type="submit" className="secondary-button" disabled={busy !== null || (videoInputs[section.id] ?? "").trim().length === 0}>
                  + Thêm video
                </button>
              </form>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
